/**
 * Pipeline Opportunity API Client
 */
import apiClient from './client';

/**
 * List all opportunities with Kanban grouping
 */
export const listOpportunities = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.stage) params.append('stage', filters.stage);
  if (filters.oem) params.append('oem', filters.oem);
  if (filters.priority) params.append('priority', filters.priority);
  if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters.include_archived) params.append('include_archived', 'true');
  
  const queryString = params.toString();
  const url = `/api/v1/pipeline/opportunities${queryString ? '?' + queryString : ''}`;
  
  const response = await apiClient.get(url);
  return response.data;
};

/**
 * Get single opportunity details
 */
export const getOpportunity = async (opportunityId) => {
  const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}`);
  return response.data;
};

/**
 * Create new opportunity
 */
export const createOpportunity = async (opportunityData) => {
  const response = await apiClient.post('/api/v1/pipeline/opportunities', opportunityData);
  return response.data;
};

/**
 * Update opportunity
 */
export const updateOpportunity = async (opportunityId, updateData) => {
  const response = await apiClient.put(`/api/v1/pipeline/opportunities/${opportunityId}`, updateData);
  return response.data;
};

/**
 * Create/connect Program 360 from a pipeline opportunity
 */
export const promoteToProgram = async (opportunityId) => {
  const response = await apiClient.post(`/api/v1/pipeline/opportunities/${opportunityId}/promote-to-program`);
  return response.data;
};

/**
 * Change opportunity stage
 */
export const changeStage = async (opportunityId, newStage, note = null) => {
  const response = await apiClient.patch(`/api/v1/pipeline/opportunities/${opportunityId}/stage`, {
    new_stage: newStage,
    note
  });
  return response.data;
};

/**
 * Archive opportunity
 */
export const archiveOpportunity = async (opportunityId) => {
  const response = await apiClient.delete(`/api/v1/pipeline/opportunities/${opportunityId}`);
  return response.data;
};

/**
 * Get pipeline statistics
 */
export const getPipelineStats = async () => {
  const response = await apiClient.get('/api/v1/pipeline/stats');
  return response.data;
};

// Re-export from shared formatters for backward compatibility
export { 
  formatCurrencyFromCents as formatCurrency, 
  formatCompactCurrency 
} from '../utils/formatters';

/**
 * Get priority color class
 */
export const getPriorityColor = (priority) => {
  const colors = {
    high: 'bg-red-100 text-red-800 border-red-300',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    low: 'bg-gray-100 text-gray-600 border-gray-300'
  };
  return colors[priority] || colors.medium;
};

/**
 * Get stage display name
 */
export const getStageDisplayName = (stage) => {
  const names = {
    target: 'Target',
    qualified: 'Qualified',
    rfq: 'RFQ',
    quoted: 'Quoted',
    awarded: 'Awarded',
    sourced: 'Sourced',
    lost: 'Lost'
  };
  return names[stage] || stage;
};

/**
 * Get stage color
 */
export const getStageColor = (stage) => {
  const colors = {
    target: 'bg-blue-50 border-blue-200',
    qualified: 'bg-purple-50 border-purple-200',
    rfq: 'bg-indigo-50 border-indigo-200',
    quoted: 'bg-cyan-50 border-cyan-200',
    awarded: 'bg-green-50 border-green-200',
    sourced: 'bg-emerald-50 border-emerald-200',
    lost: 'bg-gray-50 border-gray-200'
  };
  return colors[stage] || colors.target;
};

/**
 * Get opportunity watchers
 */
export const getWatchers = async (opportunityId) => {
  const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}/watchers`);
  return response.data;
};

/**
 * Add a watcher to an opportunity
 */
export const addWatcher = async (opportunityId, userId) => {
  const response = await apiClient.post(`/api/v1/pipeline/opportunities/${opportunityId}/watchers`, {
    user_id: userId
  });
  return response.data;
};

/**
 * Remove a watcher from an opportunity
 */
export const removeWatcher = async (opportunityId, watcherId) => {
  const response = await apiClient.delete(`/api/v1/pipeline/opportunities/${opportunityId}/watchers/${watcherId}`);
  return response.data;
};

/**
 * Get available users for watching (legacy - returns all users)
 */
export const getAvailableUsersForWatching = async (opportunityId) => {
  const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}/available-users`);
  return response.data;
};

/**
 * Search users for watching with server-side filtering (OPTIMIZED)
 */
export const searchUsersForWatching = async (opportunityId, query = '', limit = 20) => {
  const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}/users/search`, {
    params: { q: query, limit }
  });
  return response.data;
};
